import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { logout, fetchSsoProfile } from '../services/apiService';
import { getSocket, connectSocket } from '../services/socketService';
import FriendsPanel from './FriendsPanel';
import { getAuthUrl } from '../utils/authUrl';
import './Header.css';

const Header = ({ user, onShowScores, onReturnToMain, onShowUserConfig, onShowAdmin, onShowProfile, onShowMultiplayer, onShowPublicProfile }) => {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [friendsPanelOpen, setFriendsPanelOpen] = useState(false);
  const [pendingFriendCount, setPendingFriendCount] = useState(0);
  const [displayName, setDisplayName] = useState(null);

  const authUrl = getAuthUrl();

  const dropdownRef = useRef(null);
  const userButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuBtnRef = useRef(null);

  // Effect to handle clicks outside the dropdown and mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if click is outside dropdown and user button
      if (dropdownOpen && 
          dropdownRef.current && 
          userButtonRef.current && 
          !dropdownRef.current.contains(event.target) && 
          !userButtonRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }

      // Close mobile menu if click is outside mobile menu and mobile menu button
      if (mobileMenuOpen && 
          mobileMenuRef.current && 
          mobileMenuBtnRef.current && 
          !mobileMenuRef.current.contains(event.target) && 
          !mobileMenuBtnRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, mobileMenuOpen]);

  // Fetch SSO display name
  useEffect(() => {
    if (!user) { setDisplayName(null); return; }
    fetchSsoProfile()
      .then(profile => setDisplayName(profile?.display_name || null))
      .catch(() => {});
  }, [user]);

  // Fetch initial pending friend request count and listen for new requests
  useEffect(() => {
    if (!user) {
      setPendingFriendCount(0);
      return;
    }

    // Fetch initial pending count
    const fetchPendingCount = async () => {
      try {
        const response = await fetch('/api/friends/requests', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setPendingFriendCount(data.length);
        }
      } catch (error) {
        console.error('Error fetching friend requests count:', error);
      }
    };

    fetchPendingCount();

    // Listen for new friend requests via socket
    const socket = connectSocket();

    const onRequestReceived = () => {
      setPendingFriendCount(prev => prev + 1);
    };

    socket.on('friend:request-received', onRequestReceived);

    return () => {
      socket.off('friend:request-received', onRequestReceived);
    };
  }, [user]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = async () => {
    await logout();
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Handle nav link click: left-click uses SPA navigation, right/middle/ctrl opens new tab
  const handleNavClick = (e, handler) => {
    // Allow default behavior for middle-click, ctrl+click, shift+click, meta+click (new tab/window)
    if (e.button !== 0 || e.ctrlKey || e.shiftKey || e.metaKey) return;
    e.preventDefault();
    handler();
  };

  return (
    <header className="header" role="banner">
      <div className="container">
        <div className="logo-container">
          <a
            className="logo"
            href="/"
            onClick={(e) => handleNavClick(e, onReturnToMain)}
            aria-label={t('nav.home')}
            tabIndex={0}
          >
            <img src="/images/logo.png" alt={t('nav.logoAlt', 'OverGuessr - Home')} />
          </a>
        </div>

        <nav className="main-nav" role="navigation" aria-label={t('nav.menu')}>
          <ul className="nav-links" role="menubar">
            <li role="none">
              <a
                className="nav-link"
                href="/"
                onClick={(e) => handleNavClick(e, onReturnToMain)}
                role="menuitem"
                aria-label={t('nav.home')}
              >
                <i className="fas fa-home" aria-hidden="true"></i> {t('nav.home')}
              </a>
            </li>
            <li role="none">
              <a
                className="nav-link"
                href="/multiplayer"
                onClick={(e) => handleNavClick(e, onShowMultiplayer)}
                role="menuitem"
                aria-label={t('nav.multiplayer', 'Multiplayer')}
              >
                <i className="fas fa-users" aria-hidden="true"></i> {t('nav.multiplayer', 'Multiplayer')}
              </a>
            </li>
            <li role="none">
              <a
                className="nav-link"
                href="/scores"
                onClick={(e) => handleNavClick(e, onShowScores)}
                role="menuitem"
                aria-label={t('nav.scores')}
              >
                <i className="fas fa-trophy" aria-hidden="true"></i> {t('nav.scores')}
              </a>
            </li>
            {user && (
              <li role="none">
                <button
                  className="nav-link friends-link"
                  onClick={() => setFriendsPanelOpen(true)}
                  role="menuitem"
                  aria-label={t('nav.friends', 'Friends')}
                >
                  <i className="fas fa-user-friends" aria-hidden="true"></i> {t('nav.friends', 'Friends')}
                  {pendingFriendCount > 0 && <span className="friend-badge">{pendingFriendCount}</span>}
                </button>
              </li>
            )}
            <li role="none">
              <a
                className="nav-link"
                href="https://tryhard.es/"
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                aria-label={t('nav.tryhardLinkDesc', 'Go to TryHard platform')}
              >
                <i className="fas fa-gamepad" aria-hidden="true"></i> TryHard
              </a>
            </li>
            <li className="language-selector" role="none">
              <button
                className={`lang-btn ${i18n.language === 'es' ? 'active' : ''}`}
                onClick={() => changeLanguage('es')}
                aria-label={t('nav.switchToSpanish', 'Cambiar idioma a Español')}
                aria-pressed={i18n.language === 'es'}
              >
                <span className="lang-flag" aria-hidden="true">&#127466;&#127480;</span>
                <span className="lang-code">ES</span>
              </button>
              <button
                className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
                onClick={() => changeLanguage('en')}
                aria-label={t('nav.switchToEnglish', 'Change language to English')}
                aria-pressed={i18n.language === 'en'}
              >
                <span className="lang-flag" aria-hidden="true">&#127468;&#127463;</span>
                <span className="lang-code">EN</span>
              </button>
            </li>
            {user ? (
              <li className="user-info" role="none">
                <button
                  className="user-button"
                  onClick={toggleDropdown}
                  ref={userButtonRef}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="menu"
                  aria-label={t('nav.menu')}
                >
                  <span className="user-name">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="header-avatar" aria-hidden="true" />
                    ) : (
                      <i className="fas fa-user" aria-hidden="true"></i>
                    )} {user.streamer_mode ? t('userConfig.streamerMode') : (displayName || user.display_name || user.battlenet_tag || t('scores.anonymousUser'))}
                  </span>
                  <i className={`fas fa-chevron-${dropdownOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
                </button>
                {dropdownOpen && (
                  <div className="user-dropdown" ref={dropdownRef} role="menu" aria-label={t('nav.menu')}>
                    <a className="dropdown-item" href="/profile" onClick={(e) => { handleNavClick(e, () => { setDropdownOpen(false); onShowProfile(); }); }} role="menuitem">
                      <i className="fas fa-trophy" aria-hidden="true"></i> {t('nav.profile', 'Profile')}
                    </a>
                    <a className="dropdown-item" href="/config" onClick={(e) => { handleNavClick(e, () => { setDropdownOpen(false); onShowUserConfig(); }); }} role="menuitem">
                      <i className="fas fa-cog" aria-hidden="true"></i> {t('userConfig.settings')}
                    </a>
                    {(user.is_admin || (user.roles && user.roles.length > 0)) && (
                      <a className="dropdown-item" href="/admin" onClick={(e) => { handleNavClick(e, () => { setDropdownOpen(false); onShowAdmin(); }); }} role="menuitem">
                        <i className="fas fa-tools" aria-hidden="true"></i> {t('admin.title', 'Admin Panel')}
                      </a>
                    )}
                    <button className="dropdown-item" onClick={handleLogout} role="menuitem">
                      <i className="fas fa-sign-out-alt" aria-hidden="true"></i> {t('auth.logout')}
                    </button>
                  </div>
                )}
              </li>
            ) : (
              <li className="login-btn" role="none">
                <a href={authUrl} className="btn btn-bnet" role="menuitem" aria-label={t('auth.loginButton')}>
                  <i className="fas fa-sign-in-alt" aria-hidden="true"></i>
                  <span>{t('auth.loginButton')}</span>
                </a>
              </li>
            )}
          </ul>
        </nav>

        <div className="mobile-menu">
          <div className="mobile-menu-container">
            <button
              className="mobile-menu-btn"
              onClick={toggleMobileMenu}
              ref={mobileMenuBtnRef}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav"
              aria-label={t('nav.menu')}
            >
              <i className="fas fa-bars" aria-hidden="true"></i>
            </button>
          </div>
          <ul
            id="mobile-nav"
            className={`mobile-nav-links ${mobileMenuOpen ? '' : 'hidden'}`}
            ref={mobileMenuRef}
            role="menu"
            aria-label={t('nav.menu')}
          >
            <li role="none">
              <a
                className="nav-link"
                href="/"
                onClick={(e) => handleNavClick(e, onReturnToMain)}
                role="menuitem"
                aria-label={t('nav.home')}
              >
                <i className="fas fa-home" aria-hidden="true"></i> {t('nav.home')}
              </a>
            </li>
            <li role="none">
              <a
                className="nav-link"
                href="/multiplayer"
                onClick={(e) => handleNavClick(e, () => { setMobileMenuOpen(false); onShowMultiplayer(); })}
                role="menuitem"
                aria-label={t('nav.multiplayer', 'Multiplayer')}
              >
                <i className="fas fa-users" aria-hidden="true"></i> {t('nav.multiplayer', 'Multiplayer')}
              </a>
            </li>
            <li role="none">
              <a
                className="nav-link"
                href="/scores"
                onClick={(e) => handleNavClick(e, onShowScores)}
                role="menuitem"
                aria-label={t('nav.scores')}
              >
                <i className="fas fa-trophy" aria-hidden="true"></i> {t('nav.scores')}
              </a>
            </li>
            {user && (
              <li role="none">
                <button
                  className="nav-link friends-link"
                  onClick={() => { setMobileMenuOpen(false); setFriendsPanelOpen(true); }}
                  role="menuitem"
                  aria-label={t('nav.friends', 'Friends')}
                >
                  <i className="fas fa-user-friends" aria-hidden="true"></i> {t('nav.friends', 'Friends')}
                  {pendingFriendCount > 0 && <span className="friend-badge">{pendingFriendCount}</span>}
                </button>
              </li>
            )}
            <li role="none">
              <a
                className="nav-link"
                href="https://tryhard.es/"
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                aria-label={t('nav.tryhardLinkDesc', 'Go to TryHard platform')}
              >
                <i className="fas fa-gamepad" aria-hidden="true"></i> TryHard
              </a>
            </li>
            <li className="language-selector" role="none">
              <button
                className={`lang-btn ${i18n.language === 'es' ? 'active' : ''}`}
                onClick={() => changeLanguage('es')}
                role="menuitem"
                aria-label={t('nav.switchToSpanish', 'Cambiar idioma a Español')}
                aria-pressed={i18n.language === 'es'}
              >
                <span className="lang-flag" aria-hidden="true">&#127466;&#127480;</span>
                <span className="lang-code">ES</span>
              </button>
              <button
                className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
                onClick={() => changeLanguage('en')}
                role="menuitem"
                aria-label={t('nav.switchToEnglish', 'Change language to English')}
                aria-pressed={i18n.language === 'en'}
              >
                <span className="lang-flag" aria-hidden="true">&#127468;&#127463;</span>
                <span className="lang-code">EN</span>
              </button>
            </li>
            {user ? (
              <li className="mobile-user-info" role="none">
                <div className="mobile-user-name" aria-label={t('nav.currentUser', 'Usuario actual')}>
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="header-avatar" aria-hidden="true" />
                  ) : (
                    <i className="fas fa-user" aria-hidden="true"></i>
                  )} {user.streamer_mode ? t('userConfig.streamerMode') : (displayName || user.display_name || user.username || user.battlenet_tag || t('scores.anonymousUser'))}
                </div>
                <a
                  className="mobile-user-button"
                  href="/profile"
                  onClick={(e) => handleNavClick(e, () => { setMobileMenuOpen(false); onShowProfile(); })}
                  role="menuitem"
                  aria-label={t('nav.profile', 'Profile')}
                >
                  <i className="fas fa-trophy" aria-hidden="true"></i> {t('nav.profile', 'Profile')}
                </a>
                <a
                  className="mobile-user-button"
                  href="/config"
                  onClick={(e) => handleNavClick(e, () => { setMobileMenuOpen(false); onShowUserConfig(); })}
                  role="menuitem"
                  aria-label={t('userConfig.settings')}
                >
                  <i className="fas fa-cog" aria-hidden="true"></i> {t('userConfig.settings')}
                </a>
                {(user.is_admin || (user.roles && user.roles.length > 0)) && (
                  <a
                    className="mobile-user-button"
                    href="/admin"
                    onClick={(e) => handleNavClick(e, () => { setMobileMenuOpen(false); onShowAdmin(); })}
                    role="menuitem"
                    aria-label={t('admin.title', 'Admin Panel')}
                  >
                    <i className="fas fa-tools" aria-hidden="true"></i> {t('admin.title', 'Admin Panel')}
                  </a>
                )}
                <button
                  className="mobile-user-button"
                  onClick={handleLogout}
                  role="menuitem"
                  aria-label={t('auth.logout')}
                >
                  <i className="fas fa-sign-out-alt" aria-hidden="true"></i> {t('auth.logout')}
                </button>
              </li>
            ) : (
              <li id="mobile-login" role="none">
                <a
                  href={authUrl}
                  className="btn btn-bnet"
                  role="menuitem"
                  aria-label={t('auth.loginButton')}
                >
                  <i className="fas fa-sign-in-alt" aria-hidden="true"></i>
                  <span>{t('auth.loginButton')}</span>
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>

      {user && (
        <FriendsPanel
          isOpen={friendsPanelOpen}
          onClose={() => {
            setFriendsPanelOpen(false);
            // Refresh pending count when panel closes
            fetch('/api/friends/requests', { credentials: 'include' })
              .then(res => res.ok ? res.json() : [])
              .then(data => setPendingFriendCount(Array.isArray(data) ? data.length : 0))
              .catch(() => {});
          }}
          user={user}
          onNavigateToMultiplayer={(code) => {
            setFriendsPanelOpen(false);
            if (onShowMultiplayer) {
              onShowMultiplayer(code);
            }
          }}
          onShowPublicProfile={(identifier) => {
            setFriendsPanelOpen(false);
            if (onShowPublicProfile) {
              onShowPublicProfile(identifier);
            }
          }}
        />
      )}
    </header>
  );
};

export default Header;
